import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { ScrollArea } from "@workspace/web-ui/components/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/web-ui/components/select";

import { cn } from "@workspace/web-ui/lib/utils";
import * as React from "react";
import { timeZones } from "../data/time-zones";
import { useQuery } from "@tanstack/react-query";
import { useAvailableTimeSlotsQueryOptions } from "../api/queries";
import { availableTimes as availableTimesData } from "../data/available-times";
import { formatDateToLocalString } from "@workspace/lib/date";

export function RightPanel({
	timeZone,
	weeksInMonth,
	setTimeZone,
	handleChangeAvailableTime,
	selectedTime,
	selectedDate,
}: {
	timeZone: string;
	weeksInMonth: number;
	setTimeZone: (timeZone: string) => void;
	handleChangeAvailableTime: (time: string) => void;
	selectedTime?: string;
	selectedDate: Date;
}) {
	const selectedButtonRef = React.useRef<HTMLButtonElement>(null);
	const { data: availableTimes, isLoading, error } = useQuery(useAvailableTimeSlotsQueryOptions({
		date: formatDateToLocalString(selectedDate),
		timeZone: timeZone,
	}));

	const isTimeSlotPassed = React.useCallback((timeSlot: string, available: boolean): boolean => {
		if (!available) {
			return true;
		}
		const now = new Date();
		const [hoursStr, minutesWithPeriod] = timeSlot.split(':');
		const minutes = minutesWithPeriod?.slice(0, 2) ?? "00";
		const isPM = timeSlot.toLowerCase().includes('pm');

		let hour = parseInt(hoursStr ?? "0", 10);
		if (isPM && hour !== 12) {
			hour += 12;
		} else if (!isPM && hour === 12) {
			hour = 0;
		}

		const slotTime = new Date(selectedDate);
		slotTime.setHours(hour, parseInt(minutes, 10), 0);

		const isToday = now.toDateString() === selectedDate.toDateString();
		if (!isToday) {
			return false;
		}

		return now > slotTime;
	}, [selectedDate]);

	React.useEffect(() => {
		if (selectedButtonRef.current) {
			selectedButtonRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			});
		}
	}, [selectedTime]);

	return (
		<div
			className="flex flex-col gap-4 w-full  border-l px-4 py-8" style={{ height: "inherit" }}
		>
			<div className="flex items-center justify-center">
				<Select value={timeZone} onValueChange={setTimeZone}>
					<SelectTrigger className={cn(buttonVariants({ variant: "link", size: "sm", className: "p-0 text-primary font-medium	flex border-none outline-none" }))}>
						<SelectValue placeholder="Select time zone">
							{timeZone.replace(/_/g, " ").split("(")[0]?.trim()}
						</SelectValue>
					</SelectTrigger>
					<SelectContent className="w-fit dark:bg-gray-5">
						{timeZones.map((timeZone) => (
							<SelectItem
								key={timeZone.label}
								value={timeZone.tzCode}
								className="dark:focus:bg-gray-2"
							>
								{timeZone.label.replace(/_/g, " ")}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{["12"].map((time) => (
				<ScrollArea
					key={time}
					type="always"
					className="h-full"
					style={{
						maxHeight: weeksInMonth > 5 ? "380px" : "320px",
					}}
				>
					<div className="grid gap-4 pr-4">
						{isLoading ? (
							availableTimesData?.map((availableTime) => {
								const isSelected = selectedTime === availableTime[time as "12" | "24"];
								return (
									<Button
										ref={isSelected ? selectedButtonRef : null}
										variant="outline"
										disabled={isLoading}
										className={cn(
											"border-[#C9C9CC] hover:bg-transparent active:bg-primary/10 hover:border-primary/80 active:border-primary h-[56px] rounded-[12px] text-[#26282F]",
											isSelected && "bg-primary/10 border-primary",
											isLoading && "opacity-50 cursor-not-allowed",
										)}
										onClick={() =>
											handleChangeAvailableTime(
												availableTime[time as "12" | "24"],
											)
										}
										key={availableTime[time as "12" | "24"]}
									>
										{availableTime[time as "12" | "24"]}
									</Button>
								);
							})
						) : error ? (
							<div className="flex justify-center items-center h-32">
								<span className="text-red-500">Failed to load available times</span>
							</div>
						) : availableTimes?.slots?.available_time_slots?.length === 0 ? (
							availableTimesData?.map((availableTime) => {
								const isSelected = selectedTime === availableTime[time as "12" | "24"];

								return (
									<Button
										ref={isSelected ? selectedButtonRef : null}
										variant="outline"
										disabled={true}
										className={cn(
											"border-[#C9C9CC] hover:bg-transparent active:bg-primary/10 hover:border-primary/80 active:border-primary h-[56px] rounded-[12px] text-[#26282F]",
											isSelected && "bg-primary/10 border-primary",
											isLoading && "opacity-50 cursor-not-allowed",
										)}
										onClick={() =>
											handleChangeAvailableTime(
												availableTime[time as "12" | "24"],
											)
										}
										key={availableTime[time as "12" | "24"]}
									>
										{availableTime[time as "12" | "24"]}
									</Button>
								);
							})
						) : (
							availableTimes?.slots?.available_time_slots?.map((availableTime) => {
								const isSelected = selectedTime === availableTime[time as "12" | "24"];
								const isPassed = isTimeSlotPassed(availableTime[time as "12" | "24"], availableTime.available);

								return (
									<Button
										ref={isSelected ? selectedButtonRef : null}
										variant="outline"
										disabled={isPassed}
										className={cn(
											"border-[#C9C9CC] hover:bg-transparent active:bg-primary/10 hover:border-primary/80 active:border-primary h-[56px] rounded-[12px] text-[#26282F]",
											isSelected && "bg-primary/10 border-primary",
											isPassed && "opacity-50 cursor-not-allowed",
											isLoading && "opacity-50 cursor-not-allowed"
										)}
										onClick={() =>
											handleChangeAvailableTime(
												availableTime[time as "12" | "24"],
											)
										}
										key={availableTime[time as "12" | "24"]}
									>
										{availableTime[time as "12" | "24"]}
									</Button>
								);
							})
						)}
					</div>
				</ScrollArea>
			))}
		</div>
	);
}
